import React from "react";
import { ArrowRight, Wrench, Clock, ShieldCheck } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/31583977/pexels-photo-31583977.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-24 md:pt-28 pb-16 md:pb-24 bg-white overflow-hidden"
    >
      {/* diagonal stripes detail */}
      <div className="absolute inset-0 diagonal-stripes opacity-40 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        {/* LEFT */}
        <div className="lg:col-span-7 relative z-10">
          <div className="inline-flex items-center gap-2 border border-zinc-300 px-3 py-1.5 mb-6">
            <span className="w-2 h-2 bg-[var(--feniks-red)] rounded-full animate-pulse" />
            <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-700">
              Serwis czynny · Śląsk &amp; Zagłębie
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-zinc-950 mb-5"
          >
            Profesjonalny serwis
            <br />
            <span className="text-[var(--feniks-red)]">AGD i Gastronomii</span>
            <br />
            na Śląsku.
          </h1>

          <p className="font-mono text-[11px] sm:text-xs tracking-[0.2em] uppercase text-zinc-500 mb-5 border-l-2 border-[var(--feniks-red)] pl-3">
            Twój sprzęt jak Fenix powstanie z popiołów
          </p>

          <p className="text-base md:text-lg text-zinc-600 max-w-xl leading-relaxed mb-8">
            Szybka diagnoza, rzetelna naprawa. Przywracamy sprzęt do życia —
            pralki, zmywarki, piekarniki, lodówki, wyparzarki, miksery KitchenAid i więcej.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-12">
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("kontakt")}
              className="group inline-flex items-center justify-center gap-3 bg-zinc-950 text-white px-7 py-4 text-xs font-bold uppercase tracking-widest hover:bg-[var(--feniks-red)] transition-colors"
            >
              Umów serwis
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("cennik")}
              className="inline-flex items-center justify-center gap-3 border border-zinc-300 px-7 py-4 text-xs font-bold uppercase tracking-widest hover:border-zinc-900 transition-colors"
            >
              Zobacz cennik
            </button>
          </div>

          {/* Quick highlights */}
          <div className="grid grid-cols-3 border-t border-zinc-200">
            {[
              { icon: Clock, label: "Szybki dojazd", value: "Już dziś" },
              { icon: Wrench, label: "Naprawiamy", value: "12+ kategorii" },
              { icon: ShieldCheck, label: "Gwarancja", value: "Na naprawy" },
            ].map((it, i) => (
              <div
                key={i}
                className={`py-5 ${i > 0 ? "border-l border-zinc-200" : ""} pl-4 sm:pl-6 first:pl-0`}
              >
                <it.icon size={18} className="text-[var(--feniks-red)] mb-2" />
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">{it.label}</div>
                <div className="font-display text-base sm:text-lg tracking-tight">{it.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT IMAGE */}
        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] w-full overflow-hidden border border-zinc-200">
            <img
              src={HERO_IMG}
              alt="Technik podczas naprawy sprzętu"
              className="w-full h-full object-cover grayscale-[20%]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/50 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 text-white">
              <div className="text-[10px] tracking-[0.25em] uppercase opacity-80 mb-1">
                Działamy lokalnie · Katowice + 27 miast
              </div>
              <div className="font-display text-2xl tracking-tight">
                Naprawiamy. Nie wymieniamy.
              </div>
            </div>
            {/* Red corner accent */}
            <div className="absolute top-0 right-0 w-24 h-1.5 bg-[var(--feniks-red)]" />
            <div className="absolute top-0 right-0 w-1.5 h-24 bg-[var(--feniks-red)]" />
          </div>
        </div>
      </div>
    </section>
  );
}
